/**
 * Reverse-geocoding utility — turns GPS coordinates into a human-readable
 * "City, Country" label using the free, CORS-enabled BigDataCloud API.
 *
 * Why this exists
 * ───────────────
 * The app's curated POPULAR_CITIES list covers ~200 cities worldwide. A user
 * in a small city not on the list (e.g. Luleå, Sweden) used to see "Select
 * city" in the header, leading them to manually pick the nearest big city
 * (Stockholm) — which silently moved their prayer-time calculations 900 km
 * away from their actual location.
 *
 * With reverse geocoding we can show the user "📍 Luleå, Sweden" right in
 * the header, making it obvious that the app already knows where they are
 * and is using their EXACT GPS coordinates for calculations.
 *
 * No API key required. Results are cached in localStorage by rounded coords
 * (≈ 1 km grid) so we hit the network at most once per user per neighbourhood.
 */

const CACHE_KEY_PREFIX = 'falak.geocache.v1';
const CACHE_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Round to 2 decimals → ~1.1 km grid cells (avoids unnecessary refetches as
// the user moves around within their home neighbourhood). Language is part
// of the key so localized place names (e.g. "莫斯科" vs "Moscow") refresh
// when the user switches the app language.
const cacheKey = (lat, lon, lang) =>
  `${CACHE_KEY_PREFIX}.${Math.round(lat * 100) / 100}_${Math.round(lon * 100) / 100}.${lang}`;

const readCache = (lat, lon, lang) => {
  try {
    const raw = localStorage.getItem(cacheKey(lat, lon, lang));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - parsed.t > CACHE_TTL_MS) return null;
    return parsed.v;
  } catch {
    return null;
  }
};

const writeCache = (lat, lon, lang, value) => {
  try {
    localStorage.setItem(
      cacheKey(lat, lon, lang),
      JSON.stringify({ t: Date.now(), v: value })
    );
  } catch {
    /* localStorage blocked — silently continue */
  }
};

/**
 * Reverse-geocode coordinates to { city, country, countryCode }.
 * Falls back to null on network / parse errors so callers can degrade gracefully.
 *
 * @param {number} lat
 * @param {number} lon
 * @param {string} lang  ISO 639-1 code for response localisation (defaults to 'en')
 * @returns {Promise<{ city: string, country: string, countryCode: string } | null>}
 */
export const reverseGeocode = async (lat, lon, lang = 'en') => {
  if (lat == null || lon == null) return null;

  const cached = readCache(lat, lon, lang);
  if (cached) return cached;

  try {
    const url =
      `https://api.bigdatacloud.net/data/reverse-geocode-client` +
      `?latitude=${lat}&longitude=${lon}&localityLanguage=${encodeURIComponent(lang)}`;
    const res = await fetch(url);
    if (!res.ok) return null;
    const data = await res.json();

    // BigDataCloud returns multiple possible fields — prefer the most
    // user-facing one available.
    const city =
      data.city ||
      data.locality ||
      data.principalSubdivision ||
      '';
    const country = data.countryName || '';
    const countryCode = data.countryCode || '';

    if (!city && !country) return null;

    const result = { city, country, countryCode };
    writeCache(lat, lon, lang, result);
    return result;
  } catch {
    return null;
  }
};
